import { defineStore } from 'pinia';
import { DEFAULT_WAVEFORM_COLORS, ADD_EVENT_BUTTON_STATES, SHOW_TYPES, SHOW_EQUIPMENT_MANUFACTURES, PRODUCTS, MAX_NUMBER_OF_FREE_TIER_EVENTS } from '@/constants';
import { useShowStore } from './show';
import randomColor from 'randomcolor';
import { generateEventHighlightingTimeMarkers, handleUpdatingCellDurationsDuringPlayback, highlightEventsBeforeTime, resetEventHighlighting } from '@/helpers/eventHighlight';
import { useAuthStore } from './auth';

export const useWaveformStore = defineStore('waveform', {
	state: () => ({
		cue_marker_color: DEFAULT_WAVEFORM_COLORS.cue_marker_color,
		waveform_color: DEFAULT_WAVEFORM_COLORS.waveform_color,
		waveform_progress_color: DEFAULT_WAVEFORM_COLORS.waveform_progress_color,
		waveform_background_color: DEFAULT_WAVEFORM_COLORS.waveform_background_color,
		waveform_playhead_color: DEFAULT_WAVEFORM_COLORS.waveform_playhead_color,
		scroll_bar_visibility: true,
		show_cursor: true,
		on_top: true,
		current_audio_playback_time: undefined,
		mouse_position_time: undefined,
		time_remaining: undefined,
		audio_duration: undefined,
		selected_position_time: undefined,
		go_to_selected_position_time: false,
		selected_add_event_state: ADD_EVENT_BUTTON_STATES.PYRO,
		is_playing: false,
		number_of_events_to_insert: 1,
		updated_play_pause_for_step_event: false,
		hover_event: undefined,
		ADD_EVENT_BUTTON_STATES,
		current_zoom_level: 0,
		volume_level: 100,
		volume_level_before_muting: 100,
		trigger_jump_forward: false,
		trigger_jump_backward: false,
		previous_zoom_level: 0,
		update_scrollbar: false,

		are_events_being_dragged: false,
		drag_position_time: undefined,
		cancel_drag_on_esc_press: false,
		draw_step_markers: true,
		used_step_colors_names: [],
		used_step_colors: [],
		// allow_to_play_audio: false,
		trigger_persist_scroll_position: false,
	}),
	actions: {
		setEventMarkerColor(color) {
			this.cue_marker_color = color;
		},
		setWaveformColor(color) {
			this.waveform_color = color;
		},
		setWaveformProgressColor(color) {
			this.waveform_progress_color = color;
		},
		setWaveformBackgroundColor(color) {
			this.waveform_background_color = color;
		},
		setWaveformPlayheadColor(color) {
			this.waveform_playhead_color = color;
		},
		resetEventMarkerColor() {
			this.cue_marker_color = DEFAULT_WAVEFORM_COLORS.cue_marker_color;
		},
		resetWaveformColor() {
			this.waveform_color = DEFAULT_WAVEFORM_COLORS.waveform_color;
		},
		resetWaveformProgressColor() {
			this.waveform_progress_color = DEFAULT_WAVEFORM_COLORS.waveform_progress_color;
		},
		resetWaveformBackgroundColor() {
			this.waveform_background_color = DEFAULT_WAVEFORM_COLORS.waveform_background_color;
		},
		resetWaveformPlayheadColor() {
			this.waveform_playhead_color = DEFAULT_WAVEFORM_COLORS.waveform_playhead_color;
		},
		setScrollBarVisiblity(value) {
			this.scroll_bar_visibility = value;
		},
		setCursorVisibility(value) {
			this.show_cursor = value;
		},
		setWaveformLayout(value) {
			this.on_top = value;
		},
		toggleAddEventState() {
			if (useShowStore()?.active_show == undefined) {
				return;
			}

			if (useShowStore().active_show.equipment_manufacture === SHOW_EQUIPMENT_MANUFACTURES.IGNITE.key) {
				return;
			}

			if (useShowStore()?.active_show?.firmware_version >= 6 && useShowStore()?.active_show?.show_attributes?.show_type === SHOW_TYPES.DMX.key) {
				return;
			}

			if (useShowStore().active_show.firmware_version < 6) {
				switch (this.selected_add_event_state) {
					case this.ADD_EVENT_BUTTON_STATES.STEP:
						this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.PYRO;
						return;
					case this.ADD_EVENT_BUTTON_STATES.PYRO:
						this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.STEP;
						return;
				}
			} else {
				switch (this.selected_add_event_state) {
					case this.ADD_EVENT_BUTTON_STATES.STEP:
						if (useShowStore()?.active_show?.show_attributes?.show_type === SHOW_TYPES.PYRO.key) {
							this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.PYRO;
							return;
						}
						this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.DMX;
						break;
					case this.ADD_EVENT_BUTTON_STATES.DMX:
						if (useShowStore()?.active_show?.show_attributes?.show_type === SHOW_TYPES.DMX.key) {
							this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.STEP;
							return;
						}
						this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.PYRO;
						break;
					case this.ADD_EVENT_BUTTON_STATES.PYRO:
						this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.STEP;
						break;
				}
			}
		},
		setPlayback(value) {
			if (value === true) {
				// TODO: check if this function is required or not clearAllCuesCustomHighlightColor
				resetEventHighlighting();
				generateEventHighlightingTimeMarkers();
				highlightEventsBeforeTime();
				requestAnimationFrame(handleUpdatingCellDurationsDuringPlayback);
			}

			this.is_playing = value;
		},
		setCurrentPlaybackTime(value) {
			this.current_audio_playback_time = value;
		},
		decreaseNumberOfEventsToInsert() {
			if (this.number_of_events_to_insert - 1 < 1) {
				this.number_of_events_to_insert = 1;
			} else {
				this.number_of_events_to_insert -= 1;
			}
		},
		increaseNumberOfEventsToInsert() {
			if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false && this.number_of_events_to_insert + useShowStore()?.active_show?.events?.length >= MAX_NUMBER_OF_FREE_TIER_EVENTS) {
				return;
			}
			this.number_of_events_to_insert += 1;
		},
		setUpdatePausePlayIconForStepEvent(value) {
			this.updated_play_pause_for_step_event = value;
		},
		setSelectedAddEventStateBasedOnDrag() {
			if (useShowStore()?.currently_dragged_firework == null && useShowStore()?.currently_dragged_dmx != null) {
				this.selected_add_event_state = ADD_EVENT_BUTTON_STATES.DMX;
			} else {
				this.selected_add_event_state = ADD_EVENT_BUTTON_STATES.PYRO;
			}
		},
		resetSelectedAddEventState() {
			if (useShowStore()?.active_show?.firmware_version >= 6 && useShowStore()?.active_show?.show_attributes?.show_type === SHOW_TYPES.DMX.key) {
				this.selected_add_event_state = this.ADD_EVENT_BUTTON_STATES.DMX;
				return;
			}

			this.selected_add_event_state = ADD_EVENT_BUTTON_STATES.PYRO;
		},
		goToSelectedTimePosition() {
			if (this.selected_position_time != undefined) {
				this.go_to_selected_position_time = true;
			}
		},
		resetGoToSelectedTimePositionFlag() {
			this.go_to_selected_position_time = false;
		},
		zoomOut() {
			if (this.current_zoom_level - 40 >= 0) {
				this.previous_zoom_level = this.current_zoom_level;
				this.current_zoom_level -= 40;
			} else {
				this.previous_zoom_level = this.current_zoom_level;
				this.current_zoom_level = 0;
			}
		},
		zoomIn() {
			if (this.current_zoom_level + 40 <= 400) {
				this.previous_zoom_level = this.current_zoom_level;
				this.current_zoom_level += 40;
			} else {
				this.previous_zoom_level = this.current_zoom_level;
				this.current_zoom_level = 400;
			}
		},
		updateZoom(zoom_level) {
			this.previous_zoom_level = this.current_zoom_level;
			this.current_zoom_level = zoom_level;
		},
		setHoverEvent(event, count, dimensions) {
			if (!event) {
				this.hover_event = undefined;
				return;
			}

			this.hover_event = {
				event,
				count,
				dimensions,
			};
		},
		toggleJumpForward() {
			this.trigger_jump_forward = !this.trigger_jump_forward;
		},
		toggleJumpBackward() {
			this.trigger_jump_backward = !this.trigger_jump_backward;
		},
		clearAudioDuration() {
			this.audio_duration = undefined;
		},
		updateScrollbar() {
			this.update_scrollbar = !this.update_scrollbar;
		},
		updateEventsBeingsDragged(are_events_being_dragged) {
			this.are_events_being_dragged = are_events_being_dragged;
		},
		updateDragPositionTime(time) {
			this.drag_position_time = time;
		},
		cancelDragOnEscPress() {
			this.cancel_drag_on_esc_press = true;
		},
		resetCancelDrag() {
			this.cancel_drag_on_esc_press = false;
		},
		setDrawStepMarkers(value) {
			this.draw_step_markers = value;
		},

		getStepColor() {
			let color = randomColor({ hue: 'random', luminosity: 'light' });
			while (this.used_step_colors.includes(color)) {
				color = randomColor({ hue: 'random', luminosity: 'light' });
			}
			this.addColor(color, 'step');
			return color;
		},
		getNameColor() {
			let color = randomColor({ hue: 'random', luminosity: 'dark' });
			while (this.used_step_colors_names.includes(color)) {
				color = randomColor({ hue: 'random', luminosity: 'dark' });
			}
			this.addColor(color, 'names');
			return color;
		},
		addColor(color, colorType) {
			switch (colorType) {
				case 'step':
					this.used_step_colors.push(color);
					break;
				case 'names':
					this.used_step_colors_names.push(color);
					break;
				default:
					break;
			}
		},
		resetWaveformTimes() {
			this.current_audio_playback_time = undefined;
			this.mouse_position_time = undefined;
			this.selected_position_time = undefined;
		},
	},
});
